import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CurrencyDollar, ClockCountdown, UsersThree, Lightning, PaperPlaneTilt, IdentificationCard, FilePdf, UserPlus, Bell, ArrowClockwise, X as XIcon, TrendUp, Megaphone, FileText } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";
import GeneratePreviewModal from "@/components/GeneratePreviewModal";
import NewPacketModal from "@/components/NewPacketModal";
import NewProposalModal from "@/components/NewProposalModal";
import ReceivablesAging, { AgingBucketsCard } from "@/components/ReceivablesAging";
import NoWorkCard from "@/components/NoWorkCard";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { money, moneyWhole } from "@/lib/money";

const Kpi = ({ label, value, icon: Icon, tone = "slate", children }) => (
  <div className="bg-white border border-slate-200 rounded-md p-5" data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <div className="tinylabel">{label}</div>
        <div className="text-3xl font-black mt-2">{value}</div>
        {children}
      </div>
      <div className={`p-2 rounded-md bg-${tone}-100 text-${tone}-700`}><Icon size={22} weight="duotone" /></div>
    </div>
  </div>
);

const COMP_KIND = { salary: "Salary", weekly_flat: "Flat weekly rate", distribution: "Owner distribution", per_billed_hour: "Per billed hour" };

export default function Dashboard() {
  const EMPTY = { outstanding: 0, overdue: 0, pending_timesheets: 0, active_workers: 0, ready_to_invoice: { timesheet_count: 0, client_count: 0, project_count: 0, billable: 0, draft_count: 0 } };
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [s, setS] = useState(null);
  const [aging, setAging] = useState(null);
  const [pending, setPending] = useState([]);
  const [hideFresh, setHideFresh] = useState(false);
  const [busyWorker, setBusyWorker] = useState(""); // worker_id currently being nudged/reset
  const [dismissingId, setDismissingId] = useState(""); // user_id currently being dismissed
  const [showPreview, setShowPreview] = useState(false);
  const [showPacket, setShowPacket] = useState(false);
  const [showProposal, setShowProposal] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [payruns, setPayruns] = useState([]);
  const [alerts, setAlerts] = useState(null);

  const loadAlerts = () => api.get("/alerts/summary").then((r) => setAlerts(r?.data || null)).catch(() => {});
  const reloadAging = () => api.get("/reports/receivables-aging").then((r) => setAging(r?.data || null)).catch(() => {});

  const load = () => {
    api.get("/dashboard/summary").then((r) => {
      setS(r?.data && typeof r.data.outstanding === "number" ? r.data : EMPTY);
    });
    api.get("/reports/receivables-aging").then((r) => setAging(r?.data || null));
    if (isAdmin) {
      api.get("/reports/cash-flow-forecast", { params: { horizon_days: 90 } })
        .then((r) => setForecast(r?.data || null))
        .catch(() => {});
      api.get("/payruns")
        .then((r) => setPayruns(Array.isArray(r.data) ? r.data.slice(0, 4) : []))
        .catch(() => setPayruns([]));
      loadAlerts();
      api.get("/dashboard/pending-activations")
        .then((r) => setPending(Array.isArray(r.data) ? r.data : []))
        .catch(() => setPending([]));
    }
  };

  // Nudge = friendly reminder (no password change). Reset = mint new temp pw
  // and re-send the full welcome. See /workers/{id}/nudge-login and
  // /workers/{id}/create-login on the backend for the exact behaviour.
  const nudgeWorker = async (wid, mode) => {
    if (!wid || busyWorker) return;
    setBusyWorker(wid);
    const path = mode === "reset"
      ? `/workers/${wid}/create-login`
      : `/workers/${wid}/nudge-login`;
    try {
      const { data } = await api.post(path);
      const smsBit = data.sms_sent ? "SMS sent" : (data.worker_phone ? "SMS failed" : "no phone on file");
      toast.success(
        mode === "reset"
          ? `Password reset & welcome resent — email + ${smsBit}`
          : `Nudge sent — email + ${smsBit}`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally {
      setBusyWorker("");
    }
  };
  // Manually hide a contractor from the Pending First Login list.
  // Backend at /api/dashboard/pending-activations/{user_id}/dismiss flips a
  // dismissed_from_pending flag on the users doc so we stop showing it here.
  const dismissPending = async (row) => {
    if (!row?.user_id || dismissingId) return;
    if (!window.confirm(
      `Hide ${row.name || row.email} from Pending First Login?\n\n` +
      "Use this only when you know they've logged in (or you've reached out separately). This does NOT change their password. You can still nudge them from the Workers page later."
    )) return;
    setDismissingId(row.user_id);
    try {
      await api.post(`/dashboard/pending-activations/${row.user_id}/dismiss`);
      toast.success(`Hidden from pending list.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally {
      setDismissingId("");
    }
  };

  useEffect(() => { load(); }, []);
  if (!s) return <div className="p-4 md:p-8">Loading…</div>;

  const rti = s.ready_to_invoice || EMPTY.ready_to_invoice;
  const canInvoice = rti.timesheet_count > 0;
  const hasDrafts = (rti.draft_count || 0) > 0;

  return (
    <div className="p-4 md:p-8">
      <div className="tinylabel">Operations</div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Dashboard</h1>
      <p className="text-slate-500 mt-1">A live snapshot of receivables, work in progress, and payroll.</p>

      {isAdmin && (
        <div className="mt-5 flex flex-col sm:flex-row gap-3" data-testid="dashboard-hero-actions">
          <button data-testid="dashboard-new-proposal-btn" onClick={() => setShowProposal(true)}
                  className="group h-14 px-6 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-bold text-base flex items-center gap-3 shadow-sm hover:shadow transition-all">
            <span className="p-1.5 rounded-full bg-white/20"><FilePdf size={20} weight="bold" /></span>
            New Proposal
            <span className="hidden sm:inline text-xs font-normal text-teal-100">→ send to a prospective client</span>
          </button>
          <button data-testid="dashboard-new-packet-btn" onClick={() => setShowPacket(true)}
                  className="group h-14 px-6 rounded-full bg-green-700 hover:bg-green-800 text-white font-bold text-base flex items-center gap-3 shadow-sm hover:shadow transition-all">
            <span className="p-1.5 rounded-full bg-white/20"><IdentificationCard size={20} weight="bold" /></span>
            New Packet / Assignment
            <span className="hidden sm:inline text-xs font-normal text-green-100">→ existing worker or brand-new contractor</span>
          </button>
          <Link to="/recruitment?new=1" data-testid="dashboard-new-campaign-btn"
                className="group h-14 px-6 rounded-full bg-slate-800 hover:bg-slate-900 text-white font-bold text-base flex items-center gap-3 shadow-sm hover:shadow transition-all">
            <span className="p-1.5 rounded-full bg-white/20"><Megaphone size={20} weight="bold" /></span>
            New Campaign
            <span className="hidden sm:inline text-xs font-normal text-slate-300">→ recruit workers for a project</span>
          </Link>
        </div>
      )}

      {(canInvoice || hasDrafts) && (
        <div data-testid="ready-to-invoice-card"
             className="mt-6 border-2 border-green-600 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-5 shadow-sm">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-md bg-green-600 text-white">
                <Lightning size={22} weight="fill" />
              </div>
              <div>
                <div className="tinylabel text-green-800">Weekly billing</div>
                <div className="text-xl font-black text-slate-900 mt-0.5">
                  {canInvoice ? "Ready to invoice" : "All approved timesheets already invoiced"}
                </div>
                {canInvoice ? (
                  <div className="text-sm text-slate-700 mt-1" data-testid="ready-to-invoice-summary">
                    <span className="font-bold">{rti.timesheet_count}</span> approved timesheet{rti.timesheet_count === 1 ? "" : "s"} across{" "}
                    <span className="font-bold">{rti.client_count}</span> client{rti.client_count === 1 ? "" : "s"} and{" "}
                    <span className="font-bold">{rti.project_count}</span> project{rti.project_count === 1 ? "" : "s"} · estimated{" "}
                    <span className="font-mono font-black text-green-800" data-testid="ready-to-invoice-billable">{money(rti.billable)}</span> billable
                    {typeof rti.profit === "number" && (
                      <>
                        {" · "}
                        <span className={`font-mono font-black ${rti.profit < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="ready-to-invoice-profit">
                          {rti.profit < 0 ? "−" : ""}{money(Math.abs(rti.profit))}
                        </span>{" "}
                        <span className="text-slate-500">est. profit</span>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-slate-600 mt-1">
                    Nothing new to bill right now. New timesheets will show up here once supervisors approve them.
                  </div>
                )}
                {canInvoice && typeof rti.profit === "number" && (
                  <div className="text-xs text-slate-500 mt-1 font-mono" data-testid="ready-to-invoice-profit-math"
                       title={(rti.weekly_comp_lines || []).map((l) => `${l.worker_name} · ${COMP_KIND[l.kind] || l.kind} · ${money(l.amount)}`).join("\n") || "No weekly salaries or distributions"}>
                    {money(rti.billable)} billable − {money(rti.cost)} timesheet pay
                    {rti.weekly_comp > 0 && <> − <span className="text-violet-700 font-semibold" data-testid="ready-to-invoice-weekly-comp">{money(rti.weekly_comp)}</span> weekly salaries/distributions ({(rti.weekly_comp_lines || []).length})</>}
                    {" "}= est. profit
                  </div>
                )}
                {hasDrafts && (
                  <div className="text-xs text-slate-600 mt-2">
                    <span className="inline-block bg-amber-100 border border-amber-200 text-amber-800 rounded-full px-2 py-0.5 font-semibold">
                      {rti.draft_count} draft invoice{rti.draft_count === 1 ? "" : "s"} waiting to be sent
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {canInvoice && (
                <button
                  data-testid="ready-to-invoice-generate-btn"
                  onClick={() => setShowPreview(true)}
                  className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-md text-sm flex items-center gap-2"
                >
                  <Lightning size={18} weight="bold" /> Preview &amp; generate
                </button>
              )}
              {hasDrafts && (
                <Link
                  to="/invoices?filter=draft"
                  data-testid="ready-to-invoice-drafts-link"
                  className="h-11 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-md text-sm flex items-center gap-2"
                >
                  <PaperPlaneTilt size={18} weight="bold" /> Send drafts →
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Row 0: Alerts (bounced emails + expiring certs) */}
      {isAdmin && alerts && ((alerts.bounced_emails || []).length > 0 || (alerts.expiring_certs || []).length > 0 || (alerts.no_approver_timesheets || []).length > 0 || (alerts.supervisor_suggestions || []).length > 0) && (
        <AlertsCard alerts={alerts} onRefresh={loadAlerts} />
      )}

      {/* Row 1: Outstanding KPI + Receivables aging widget (spans 3 cols) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <Kpi label="Outstanding" value={money(s.outstanding)} icon={CurrencyDollar} tone="orange">
          <div className="text-[11px] text-slate-500 mt-1">sent invoices, unpaid balance</div>
          {(s.drafts_unsent_count || 0) > 0 && (
            <Link to="/invoices?filter=draft" data-testid="drafts-unsent-chip"
              title="Draft invoices are not counted as outstanding until they are sent. Click to review and send them."
              className="mt-2 inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200">
              <FileText size={12} weight="bold" /> Drafts not yet sent: {money(s.drafts_unsent_total)} ({s.drafts_unsent_count})
            </Link>
          )}
        </Kpi>
        <div className="md:col-span-3">
          <AgingBucketsCard data={aging} onRefresh={reloadAging} />
        </div>
      </div>

      {/* Row 2: Last 4 Pay Runs */}
      {isAdmin && payruns.length > 0 && <RecentPayRunsCard payruns={payruns} />}

      {/* Row 3: Cash Flow Forecast */}
      {isAdmin && forecast && (
        <DashCashFlowCard forecast={forecast} />
      )}

      {/* Row 3: Pending Timesheets + Active Workers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <Kpi label="Pending Timesheets" value={s.pending_timesheets} icon={ClockCountdown} tone="yellow" />
        <Kpi label="Active Workers" value={s.active_workers} icon={UsersThree} tone="slate" />
      </div>

      {isAdmin && pending.length > 0 && (() => {
        const visible = hideFresh
          ? pending.filter((p) => (p.days_since_login_created ?? 99) > 1)
          : pending;
        return (
          <div data-testid="pending-activations-card"
               className="mt-4 border-2 border-amber-400 bg-amber-50 rounded-lg p-5 shadow-sm">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-md bg-amber-500 text-white">
                  <UserPlus size={22} weight="fill" />
                </div>
                <div>
                  <div className="tinylabel text-amber-900">Pending first login</div>
                  <div className="text-xl font-black text-slate-900 mt-0.5">
                    {visible.length} contractor{visible.length === 1 ? "" : "s"} haven't logged in yet
                  </div>
                  <div className="text-sm text-slate-700 mt-1">
                    Their login was created but they haven't successfully signed in a first time.
                    {hideFresh && pending.length !== visible.length && (
                      <span className="text-slate-500"> ({pending.length - visible.length} hidden — under 24h old)</span>
                    )}
                  </div>
                </div>
              </div>
              <label className="text-xs text-slate-600 flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hideFresh}
                  onChange={(e) => setHideFresh(e.target.checked)}
                  data-testid="pending-activations-hide-fresh"
                  className="rounded"
                />
                Hide ≤ 24h old
              </label>
            </div>
            {visible.length > 0 && (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-amber-200">
                      <th className="py-2 pr-4">Worker</th>
                      <th className="py-2 pr-4">Email</th>
                      <th className="py-2 pr-4">Phone</th>
                      <th className="py-2 pr-4">Login created</th>
                      <th className="py-2 pr-4 text-right">Days waiting</th>
                      <th className="py-2 pr-4 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((p) => {
                      const busy = busyWorker === p.worker_id;
                      return (
                        <tr key={p.user_id} data-testid={`pending-activation-${p.user_id}`}
                            className="border-b border-amber-100 last:border-none">
                          <td className="py-2 pr-4 font-semibold">
                            {p.worker_id ? (
                              <Link to={`/workers?highlight=${p.worker_id}`} className="text-slate-900 hover:text-amber-700">
                                {p.name}
                              </Link>
                            ) : p.name}
                          </td>
                          <td className="py-2 pr-4 text-slate-600">{p.email}</td>
                          <td className="py-2 pr-4 text-slate-600 font-mono text-xs">{p.phone || "—"}</td>
                          <td className="py-2 pr-4 text-slate-500 text-xs">
                            {p.login_created_at ? new Date(p.login_created_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="py-2 pr-4 text-right font-mono font-bold">
                            {p.days_since_login_created != null ? p.days_since_login_created.toFixed(1) : "—"}
                          </td>
                          <td className="py-2 pr-4 text-right">
                            <div className="inline-flex gap-1.5">
                              <button
                                type="button"
                                data-testid={`nudge-btn-${p.worker_id}`}
                                onClick={() => nudgeWorker(p.worker_id, "nudge")}
                                disabled={busy || !p.worker_id}
                                title="Send friendly reminder — email + SMS. Does NOT change their temp password."
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded border border-amber-400 bg-white text-amber-800 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Bell size={13} weight="fill" />
                                {busy ? "…" : "Nudge"}
                              </button>
                              <button
                                type="button"
                                data-testid={`reset-btn-${p.worker_id}`}
                                onClick={() => {
                                  if (window.confirm(
                                    `Reset ${p.name}'s temp password and re-send the full welcome email + SMS?\n\n` +
                                    "Their old temp password will stop working. Use this only if they lost the original welcome email."
                                  )) nudgeWorker(p.worker_id, "reset");
                                }}
                                disabled={busy || !p.worker_id}
                                title="Generate a NEW temp password and resend the full welcome. Their old password stops working."
                                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded border border-slate-400 bg-white text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ArrowClockwise size={13} weight="fill" />
                                {busy ? "…" : "Reset & resend"}
                              </button>
                              <button
                                type="button"
                                data-testid={`dismiss-pending-${p.user_id}`}
                                onClick={() => dismissPending(p)}
                                disabled={dismissingId === p.user_id || busy}
                                title="Hide this contractor from Pending First Login (they've logged in outside our tracking, or you've handled them separately). Does NOT reset their password."
                                aria-label={`Dismiss ${p.name} from pending`}
                                className="inline-flex items-center justify-center p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors disabled:opacity-50"
                              >
                                <XIcon size={14} weight="bold" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}

      <NoWorkCard />

      <ReceivablesAging data={aging} onRefresh={reloadAging} />

      {showPreview && <GeneratePreviewModal onClose={() => setShowPreview(false)} onGenerated={load} />}
      {showPacket && <NewPacketModal onClose={() => setShowPacket(false)} onCreated={() => {}} />}
      {showProposal && <NewProposalModal onClose={() => setShowProposal(false)} onCreated={() => {}} />}
    </div>
  );
}

function AlertsCard({ alerts, onRefresh }) {
  const bounced = alerts.bounced_emails || [];
  const certs = alerts.expiring_certs || [];
  const noApprover = alerts.no_approver_timesheets || [];
  const sugs = alerts.supervisor_suggestions || [];
  const [sugBusy, setSugBusy] = useState(null);
  const [welcomePrompt, setWelcomePrompt] = useState(null); // { sid, supervisor_name, project_name, supervisor_is_new }
  const [welcomeBusy, setWelcomeBusy] = useState(false);
  const actSuggestion = async (id, action) => {
    setSugBusy(id);
    try {
      const r = await api.post(`/supervisor-suggestions/${id}/${action}`);
      if (action === "add" && r.data?.ok) {
        setWelcomePrompt({ sid: id, ...r.data });
      } else {
        onRefresh();
      }
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setSugBusy(null); }
  };
  const closeWelcomePrompt = () => { setWelcomePrompt(null); onRefresh(); };
  const sendWelcome = async () => {
    setWelcomeBusy(true);
    try {
      const r = await api.post(`/supervisor-suggestions/${welcomePrompt.sid}/send-welcome`);
      toast.success(`Welcome email sent to ${r.data?.sent_to || welcomePrompt.supervisor_name}.`);
      closeWelcomePrompt();
    } catch (e) { toast.error(e.response?.data?.detail?.toString() || e.message); }
    finally { setWelcomeBusy(false); }
  };
  const ack = async (id) => {
    try { await api.post(`/alerts/bounces/${id}/ack`); onRefresh(); } catch { /* noop */ }
  };
  const fmt = (iso) => iso ? new Date(iso).toLocaleDateString() : "—";
  return (
    <div className="mb-4 bg-white border-2 border-rose-300 rounded-md p-5" data-testid="alerts-card">
      <div className="tinylabel text-rose-600">Needs attention</div>
      {welcomePrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="welcome-prompt-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-emerald-700">✓ {welcomePrompt.supervisor_name} added to {welcomePrompt.project_name}</h3>
            <p className="mt-2 text-sm text-slate-600" data-testid="welcome-prompt-text">
              {welcomePrompt.supervisor_is_new
                ? "They haven't supervised for us before. Send them the welcome email explaining how electronic timesheet approvals work?"
                : "They've supervised for us before, so they already know how approvals work. Send the welcome email anyway?"}
            </p>
            <div className="mt-5 flex gap-2 justify-end">
              <button data-testid="welcome-skip-btn" disabled={welcomeBusy} onClick={closeWelcomePrompt}
                className="h-10 px-4 border border-slate-300 rounded-md font-semibold text-sm hover:bg-slate-50">
                {welcomePrompt.supervisor_is_new ? "Skip" : "No, they know the drill"}
              </button>
              <button data-testid="welcome-send-btn" disabled={welcomeBusy} onClick={sendWelcome}
                className={`h-10 px-4 rounded-md font-semibold text-sm text-white disabled:opacity-50 ${welcomePrompt.supervisor_is_new ? "bg-emerald-600 hover:bg-emerald-700" : "bg-slate-600 hover:bg-slate-700"}`}>
                {welcomeBusy ? "Sending…" : "Send welcome email"}
              </button>
            </div>
          </div>
        </div>
      )}
      {sugs.length > 0 && (
        <div className="mt-2">
          <h2 className="text-lg font-bold text-emerald-700">{sugs.length} suggested approver{sugs.length === 1 ? "" : "s"} from workers</h2>
          <div className="mt-2 divide-y divide-emerald-100 border border-emerald-200 rounded-md">
            {sugs.map((s) => (
              <div key={s.id} className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid={`suggestion-row-${s.id}`}>
                <span className="font-semibold">{s.name}</span>
                <span className="text-slate-600">{s.email}{s.phone ? ` · ${s.phone}` : ""}</span>
                <span className="text-xs text-slate-400">for {s.project_name} — sent by {s.worker_name}</span>
                <span className="ml-auto flex gap-2">
                  <button data-testid={`suggestion-add-${s.id}`} disabled={sugBusy === s.id}
                          onClick={() => actSuggestion(s.id, "add")}
                          className="px-2.5 py-1 rounded bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold disabled:opacity-50">
                    {sugBusy === s.id ? "Adding…" : "Add to project"}
                  </button>
                  <button data-testid={`suggestion-dismiss-${s.id}`} disabled={sugBusy === s.id}
                          onClick={() => actSuggestion(s.id, "dismiss")}
                          className="px-2.5 py-1 rounded border border-slate-300 text-slate-600 text-xs font-bold hover:bg-slate-50 disabled:opacity-50">
                    Dismiss
                  </button>
                </span>
              </div>
            ))}
          </div>
          <div className="mt-1 text-xs text-slate-500">"Add to project" makes them the approving supervisor and emails/texts the worker that they can now submit.</div>
        </div>
      )}
      {noApprover.length > 0 && (
        <div className="mt-2">
          <h2 className="text-lg font-bold text-rose-700">{noApprover.length} timesheet{noApprover.length === 1 ? "" : "s"} with no approver</h2>
          <div className="mt-2 divide-y divide-rose-100 border border-rose-200 rounded-md">
            {noApprover.map((t) => (
              <div key={t.timesheet_id} className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid={`no-approver-row-${t.timesheet_id}`}>
                <span className="font-semibold">{t.worker_name}</span>
                <span className="text-slate-600">{t.project_name}</span>
                <span className="text-xs text-slate-400">week of {t.week_start} · {t.total_hours}h</span>
                <span className="ml-auto text-xs font-bold">
                  {t.project_has_supervisor_now
                    ? <Link to="/timesheets" className="text-rose-700 hover:underline">Resend approval →</Link>
                    : <Link to="/projects" className="text-rose-700 hover:underline">Add a supervisor →</Link>}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-1 text-xs text-slate-500">No approval link could be sent for these — add a supervisor to the project, then click "Resend approval" on the Timesheets tab (or manually approve with a reason).</div>
        </div>
      )}
      {bounced.length > 0 && (
        <div className="mt-2">
          <h2 className="text-lg font-bold text-rose-700">{bounced.length} invoice email{bounced.length === 1 ? "" : "s"} bounced</h2>
          <div className="mt-2 divide-y divide-rose-100 border border-rose-200 rounded-md">
            {bounced.map((b) => (
              <div key={b.id} className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid={`bounce-row-${b.id}`}>
                {b.invoice_id
                  ? <Link to={`/invoices/${b.invoice_id}`} className="font-mono font-bold text-rose-700 hover:underline">{b.invoice_number || "invoice"}</Link>
                  : <span className="font-mono font-bold">{b.invoice_number || "—"}</span>}
                <span className="text-slate-600">to <b>{b.to}</b></span>
                <span className="text-xs text-slate-400">{fmt(b.created_at)} · {b.delivery_status}</span>
                <button onClick={() => ack(b.id)} data-testid={`bounce-ack-${b.id}`}
                        className="ml-auto text-xs font-semibold px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">
                  Dismiss
                </button>
              </div>
            ))}
          </div>
          <div className="mt-1 text-xs text-slate-500">These emails did not reach the client — check the address on the client record and resend.</div>
        </div>
      )}
      {certs.length > 0 && (
        <div className="mt-3">
          <h2 className="text-lg font-bold text-amber-700">{certs.length} certification{certs.length === 1 ? "" : "s"} expiring or expired</h2>
          <div className="mt-2 divide-y divide-amber-100 border border-amber-200 rounded-md">
            {certs.map((c) => (
              <div key={c.cert_id} className="px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm" data-testid={`cert-alert-${c.cert_id}`}>
                <span className="font-semibold">{c.worker_name}</span>
                <span className="text-slate-600">{c.name}</span>
                <span className={`text-xs font-bold ${c.days_left < 0 ? "text-rose-600" : "text-amber-600"}`}>
                  {c.days_left < 0 ? `EXPIRED ${c.expires_on}` : `expires ${c.expires_on} · ${c.days_left}d left`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RecentPayRunsCard({ payruns }) {
  // Combine runs that share a week so a catch-up run doesn't skew per-week profit.
  const weeks = [];
  for (const p of payruns) {
    let g = weeks.find((w) => w.week_start === p.week_start);
    if (!g) { g = { week_start: p.week_start, runs: [], total: 0, invoiced_total: 0 }; weeks.push(g); }
    g.runs.push(p);
    g.total += p.total || 0;
    g.invoiced_total += p.invoiced_total || 0;
  }
  return (
    <div className="mt-4 bg-white border border-violet-200 rounded-md p-5" data-testid="recent-payruns-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="tinylabel text-violet-700">Payroll</div>
          <h2 className="text-xl font-bold">Last {weeks.length} Pay Week{weeks.length === 1 ? "" : "s"}</h2>
        </div>
        <Link to="/payruns" data-testid="recent-payruns-link" className="text-sm font-semibold text-violet-700 hover:text-violet-900">All pay runs →</Link>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-4 text-right" title="Profit ÷ Invoiced Total">Margin</th>
              <th className="py-2 pr-4">Week</th>
              <th className="py-2 pr-4">Status</th>
              <th className="py-2 pr-4 text-right">Pay Total</th>
              <th className="py-2 pr-4 text-right">Invoiced Total</th>
              <th className="py-2 text-right">Profit</th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((g) => {
              const profit = g.invoiced_total - g.total;
              const margin = g.invoiced_total > 0 ? (profit / g.invoiced_total) * 100 : null;
              const p = g.runs[0];
              return (
                <tr key={g.week_start} className="border-b border-slate-100 last:border-none" data-testid={`recent-payrun-${p.id}`}>
                  <td className={`py-2 pr-4 text-right font-mono font-black ${margin == null ? "text-slate-300" : margin < 0 ? "text-red-600" : margin < 20 ? "text-amber-600" : "text-emerald-700"}`}
                      data-testid={`recent-payrun-margin-${p.id}`} title={margin == null ? "No invoiced total for this week" : `${money(profit)} ÷ ${money(g.invoiced_total)}`}>
                    {margin == null ? "—" : `${margin.toFixed(1)}%`}
                  </td>
                  <td className="py-2 pr-4 font-semibold">
                    {g.week_start}
                    {g.runs.length > 1 && <span className="ml-2 text-[10px] font-black uppercase tracking-wider bg-violet-100 text-violet-700 rounded-full px-2 py-0.5">{g.runs.length} runs</span>}
                  </td>
                  <td className="py-2 pr-4"><div className="flex flex-wrap gap-1">{g.runs.map((r) => <StatusPill key={r.id} status={r.status} />)}</div></td>
                  <td className="py-2 pr-4 text-right font-mono">{money(g.total)}</td>
                  <td className="py-2 pr-4 text-right font-mono">{money(g.invoiced_total)}</td>
                  <td className={`py-2 text-right font-mono font-semibold ${profit > 0 ? "text-green-600" : profit < 0 ? "text-red-600" : "text-slate-400"}`}
                      data-testid={`recent-payrun-profit-${p.id}`}>
                    {profit > 0 ? "+" : ""}{money(profit)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DashCashFlowCard({ forecast }) {
  const money = moneyWhole;
  const short = (v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`;
  const has = forecast?.unpaid_count > 0;
  return (
    <div className="mt-4 bg-gradient-to-br from-teal-50 to-emerald-50 border border-teal-200 rounded-md p-5" data-testid="dash-cash-flow-card">
      <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="tinylabel text-teal-700">Forecast</div>
          <h2 className="text-xl font-bold">Projected Cash Flow — Next 90 Days</h2>
          <p className="text-xs text-slate-500 mt-0.5">Based on each client's historic days-to-pay.</p>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Outstanding</div>
          <div className="text-2xl font-black text-slate-900">{money(forecast.total_outstanding)}</div>
          <div className="text-[11px] text-slate-500">{forecast.unpaid_count} unpaid invoice{forecast.unpaid_count === 1 ? "" : "s"}</div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        {[{ label: "Next 30 days", val: forecast.expected_30d }, { label: "Next 60 days", val: forecast.expected_60d }, { label: "Next 90 days", val: forecast.expected_90d }].map(({ label, val }) => (
          <div key={label} className="bg-white rounded-md p-3 border border-teal-100 text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{label}</div>
            <div className="text-xl font-black text-teal-700 mt-1">{money(val)}</div>
          </div>
        ))}
      </div>
      {has && forecast?.weeks?.length ? (
        <div className="h-36 bg-white rounded-md p-2 border border-teal-100">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={forecast.weeks}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="week_start" tick={{ fontSize: 10 }} tickFormatter={(v) => v?.slice(5)} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={short} />
              <Tooltip formatter={(v) => money(v)} labelFormatter={(v) => `Week of ${v}`} />
              <Line type="monotone" dataKey="amount" stroke="#0f766e" strokeWidth={2.5} dot={{ r: 3 }} name="Expected" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="bg-white rounded-md p-4 text-center text-slate-400 text-sm border border-teal-100">
          {has ? "Forecast chart loading…" : "No outstanding invoices — you're all caught up."}
        </div>
      )}
    </div>
  );
}
